<template>
    <div :class="wrapperClass">
        <!-- Widget Actions - Export Buttons in 3-dots menu -->
        <portal :to="`widget-actions__${widgetData.WidgetID}`">
            <!-- Export DOCX -->
            <div>
                <div
                    class="flex items-center p-2 cursor-pointer hover:bg-gray-50"
                    :class="{ 'opacity-50 cursor-not-allowed': !hasContent || isExporting }"
                    @click="$emit('export-docx')"
                >
                    <redesign-base-icon
                        :name="isExporting ? 'Loader2' : 'Download'"
                        :class="{ 'animate-spin': isExporting }"
                        class="text-active-elements"
                    />
                    <span class="mx-1 hover:text-active-elements">
                        {{ isExporting ? $t('widget.ai.exporting') : $t('widget.ai.exportDocx') }}
                    </span>
                </div>

                <!-- Download MD -->
                <div
                    class="flex items-center p-2 cursor-pointer hover:bg-gray-50"
                    :class="{ 'opacity-50 cursor-not-allowed': !hasContent || isDownloadingMd }"
                    @click="$emit('download-md')"
                >
                    <redesign-base-icon
                        :name="isDownloadingMd ? 'Loader2' : 'FileText'"
                        :class="{ 'animate-spin': isDownloadingMd }"
                        class="text-active-elements"
                    />
                    <span class="mx-1 hover:text-active-elements">
                        {{ isDownloadingMd ? $t('widget.ai.downloading') : $t('widget.ai.downloadMd') }}
                    </span>
                </div>
            </div>
        </portal>

        <!-- Widget Header Action - Regenerate Button -->
        <portal :to="`widget-header__${widgetData.WidgetID}-action`">
            <RedesignBaseButton
                v-if="!isRegenerating"
                size="mini"
                type="outline"
                color="primary"
                icon="RefreshCw"
                @click="$emit('regenerate')"
            >
                {{ $t('widget.ai.regenerate') }}
            </RedesignBaseButton>
            <RedesignBaseButton
                v-else
                size="mini"
                type="outline"
                color="primary"
                icon="Loader2"
                disabled
            >
                {{ $t('widget.ai.regenerating') }}
            </RedesignBaseButton>
        </portal>

        <!-- Analysis Progress Indicator -->
        <div class="mb-4 flex items-stretch gap-2">
            <RedesignMetricCard
                :label="$t('widget.timestamp.period')"
                :value="periodDisplayValue"
            />
            <RedesignMetricCard
                :label="$t('widget.ai.totalCalls')"
                :value="callCount"
            />
            <RedesignMetricCard
                :label="$t('widget.ai.analyzed')"
                :value="includedCallCount"
                value-variant="success"
            />
            <RedesignMetricCard
                :label="$t('widget.ai.pending')"
                :value="skippedCallCount"
                value-variant="danger"
            />
            <RedesignMetricCard width="auto">
                <CircularProgress
                    :percentage="analysisProgress"
                    color="#0DA465"
                />
            </RedesignMetricCard>
        </div>

        <!-- Content Slot -->
        <slot v-if="hasContent" />

        <!-- Empty State -->
        <div
            v-else
            class="summary-empty flex flex-col items-center justify-center h-full gap-2"
        >
            {{ $t('widget.noDataAvailableYet') }}
            <AILoader />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { WidgetExtended } from '@/types/dashboard'
import i18n from '@/i18n'
import { getWidgetTemplate } from '@/composables/globalData/useTemplatesCategoryData'
import { PERIOD_TYPE_PARAMETER_NAME } from '@/enum/widgetTemplateConfigs'
import { widgetTimeOptionsForPeriod } from '@/enum/widgetTimeOptions'
import CircularProgress from '@/components/RedesignComponents/Widgets/Common/CircularProgress.vue'
import RedesignMetricCard from '@/components/RedesignComponents/Widgets/Common/RedesignMetricCard.vue'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import AILoader from '@/components/Common/AILoader.vue'

interface Props {
    widgetData: WidgetExtended
    hasContent: boolean
    callCount: number
    includedCallCount: number
    skippedCallCount: number
    analysisProgress: number
    isRegenerating: boolean
    isExporting: boolean
    isDownloadingMd: boolean
    wrapperClass?: string
}

const props = defineProps<Props>()

const periodDisplayValue = computed(() => {
    const config = props.widgetData.WidgetConfig
        ?.find(item => item.ParameterName === PERIOD_TYPE_PARAMETER_NAME)
    if (!config?.WidgetParameterValue) return ''

    const value = String(config.WidgetParameterValue)
    const options = getWidgetTemplate(props.widgetData.TemplateID)?.DefaultWidgetConfig
        ?.find(item => item.ParameterID === config.ParameterID)?.ParameterData?.data

    const label = options?.find((item: { Key?: string; Value?: string }) => item.Value === value)?.Key ?? value
    const option = widgetTimeOptionsForPeriod.find(item => item.value === label.toLowerCase())

    return option ? String(i18n.t(option.label)) : label
})

defineEmits<{
    (e: 'regenerate'): void
    (e: 'export-docx'): void
    (e: 'download-md'): void
}>()
</script>

<style lang="scss" scoped>
.summary-renderer {
  padding: 16px;
  border-radius: 8px;
  background-color: var(--mainBgColor-color);
  border: 1px solid var(--colorWidgetBorder-color);
}
</style>
